/**
 * The one place that knows whether we are inside the Tauri shell.
 *
 * The same bundle is served to a browser and loaded by the desktop window — that
 * is the whole architecture of `apps/desktop`, and it only works if nothing in
 * here is *required* at load time. So `@tauri-apps/api` is reached exclusively
 * through `import()`, which Vite emits as a separate chunk: a browser tab never
 * requests it, and a browser build with the package absent still builds and runs.
 * A static import would put the Tauri IPC shim in the main chunk for every reader
 * on the web, and break the build for anyone who installs without it.
 *
 * Every call is also *soft*. Native features are additive — silent key restore,
 * deep links, notifications, uncensored NIP-05 — so a failure here means the app
 * behaves like the web build, never that a screen breaks. Nothing in this module
 * throws.
 */

/**
 * Whether this document is running inside the desktop shell.
 *
 * `__TAURI_INTERNALS__` rather than `window.__TAURI__`: the latter only exists when
 * `withGlobalTauri` is on, and `tauri.conf.json` deliberately turns it off so a
 * script that reaches the page finds no global bridge. The internals object is
 * always injected and is what `@tauri-apps/api` itself talks to.
 *
 * Constant for the life of the document: a page cannot move between a webview and
 * a browser tab, so callers may branch on this outside of React state and mount
 * components conditionally on it without risking a hook-order change.
 */
export function isDesktopShell(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/**
 * Call one of the shell's commands.
 *
 * Resolves `undefined` when there is no shell, when the API cannot be loaded, or
 * when the command itself failed. Callers cannot distinguish those and must not
 * need to: each one means "this native capability is not available right now", and
 * the fallback is whatever the browser does.
 */
export async function invokeNative<T>(
  command: string,
  args?: Record<string, unknown>,
): Promise<T | undefined> {
  if (!isDesktopShell()) return undefined;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    return await invoke<T>(command, args);
  } catch {
    return undefined;
  }
}

/**
 * Listen for an event emitted by the Rust side.
 *
 * Returns a teardown function — a no-op one when there is no shell — so a caller
 * can unsubscribe unconditionally in an effect cleanup.
 *
 * The unsubscribe is deliberately not awaited by callers: `listen` resolves after
 * a round trip through the IPC, so an effect that unmounts in the same tick as it
 * mounts (React strict mode does exactly this) has to be able to cancel a
 * subscription that does not exist yet. Hence the `cancelled` flag.
 */
export function listenNative<T>(
  event: string,
  handler: (payload: T) => void,
): () => void {
  if (!isDesktopShell()) return () => {};

  let cancelled = false;
  let stop: (() => void) | undefined;

  void (async () => {
    try {
      const { listen } = await import("@tauri-apps/api/event");
      const unlisten = await listen<T>(event, (received) => {
        if (!cancelled) handler(received.payload);
      });
      if (cancelled) {
        unlisten();
        return;
      }
      stop = unlisten;
    } catch {
      // No listener installed. The caller's cleanup below is still safe to call.
    }
  })();

  return () => {
    cancelled = true;
    stop?.();
  };
}
